import { useId, type ReactNode } from "react";
import { paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { cn } from "../lib/cn";

export interface SparklineProps {
  /** Ordered series values (oldest → newest). */
  data: number[];
  width?: number;
  height?: number;
  /**
   * Color mode:
   * - `'auto'` (default): green when last ≥ first, red otherwise.
   * - `'neutral'`: brand blue.
   * - explicit hex string.
   */
  color?: "auto" | "neutral" | string;
  /** Fill a soft gradient under the line. */
  area?: boolean;
  theme?: Theme;
  className?: string;
  ariaLabel?: string;
}

/**
 * Dependency-free inline sparkline (pure SVG) — crisper and lighter than a
 * charting lib at 24px tall, ideal for per-strategy P&L cells. Baseline-anchored,
 * 2px stroke, with an optional gradient area fill.
 */
export function Sparkline({
  data,
  width = 96,
  height = 28,
  color = "auto",
  area = true,
  theme,
  className,
  ariaLabel,
}: SparklineProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const gradientId = useId();

  if (data.length === 0) {
    return (
      <svg
        width={width}
        height={height}
        className={cn("block", className)}
        role="img"
        aria-label={ariaLabel ?? "No data"}
      />
    );
  }

  const first = data[0]!;
  const last = data[data.length - 1]!;
  const stroke =
    color === "auto"
      ? last >= first
        ? palette.good
        : palette.critical
      : color === "neutral"
        ? palette.categorical[0]!
        : color;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;

  const points = data.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + innerH - ((v - min) / span) * innerH;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");
  const areaPath = `${linePath} L${points[points.length - 1]!.x.toFixed(2)},${(
    height - pad
  ).toFixed(2)} L${points[0]!.x.toFixed(2)},${(height - pad).toFixed(2)} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn("block overflow-visible", className)}
      role="img"
      aria-label={ariaLabel ?? "Trend sparkline"}
      preserveAspectRatio="none"
    >
      {area && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        </>
      )}
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={points[points.length - 1]!.x}
        cy={points[points.length - 1]!.y}
        r={2.5}
        fill={stroke}
      />
    </svg>
  );
}
